import type { UserPermissions } from './permissions';

export enum AutomationType {
  CRON = 'CRON',
  WEBHOOK = 'WEBHOOK',
}

export interface Automation {
  id: string;
  name: string;
  description: string | null;
  type: AutomationType;
  isActive: boolean;
  schedule: string | null;
  slug: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationWithStats extends Automation {
  stats: {
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
    successRate: number;
    totalTimeSavedMinutes: number;
    lastExecution: Execution | null;
  };
}

export interface CreateAutomationInput {
  name: string;
  description?: string;
  type?: AutomationType;
  isActive: boolean;
  schedule?: string;
  slug?: string;
  config?: Record<string, unknown>;
}

export enum ExecutionStatus {
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  TIMEOUT = 'TIMEOUT',
  CANCELLED = 'CANCELLED',
}

export interface Execution {
  id: string;
  automationId: string;
  status: ExecutionStatus;
  startedAt: string;
  finishedAt: string | null;
  duration: number | null;
  error: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  automation?: Automation;
}

export interface ExecutionStats {
  total: number;
  success: number;
  error: number;
  running: number;
  successRate: number;
  totalTimeSavedMinutes: number;
}

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

// Aggregated log entry (stored in Execution.logs JSONB array)
export interface Log {
  level: LogLevel;
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

export interface ApiError {
  error: {
    message: string;
    code: string;
  };
  timestamp: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface HealthStatus {
  status: 'ok' | 'error';
  timestamp: string;
  uptime?: number;
  env?: string;
  services?: {
    database: string;
    redis: string;
    worker: string;
  };
}

// API Request Types
export interface AutomationListParams {
  isActive?: boolean;
  includeStats?: boolean;
}

export interface ExecutionListParams {
  automationId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface ExecutionLogsParams {
  level?: string;
  limit?: number;
}

// API Error Types
export interface AxiosErrorResponse {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
    status?: number;
  };
  message: string;
}

// Job Types
export interface JobMetadata {
  category: string;
  tags: string[];
  icon?: string;
  color?: string;
  author?: string;
  version?: string;
  configFields?: JobConfigField[];
  exampleConfig?: Record<string, unknown>;
  requiredEnvVars?: string[];
}

export interface JobConfigField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'select';
  description: string;
  required: boolean;
  default?: unknown;
  placeholder?: string;
  group?: string;
  order?: number;
  validation?: {
    min?: number;
    max?: number;
  };
  options?: Array<{
    label: string;
    value: unknown;
  }>;
}

export interface Job {
  id: string;
  name: string;
  description: string;
  metadata: JobMetadata;
}

// Form Types
export interface AutomationFormData {
  type: AutomationType;
  name: string;
  description?: string;
  slug?: string;
  isActive: boolean;
  schedule?: string;
  jobType?: string;
  baseTimeMinutes?: number;
  timePerUnitMinutes?: number;
  config?: string;
}

// Payment Types
export enum PaymentStatus {
  PENDING = 'PENDING',
  PHONE_VERIFIED = 'PHONE_VERIFIED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface Payment {
  id: string;
  email: string;
  phone: string;
  status: PaymentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentFormData {
  name: string;
  email: string;
  phone: string;
}

// Verification Code Types
export interface VerificationCode {
  code: string;
  expiresAt: string;
  isVerified: boolean;
}

export interface SendVerificationCodeRequest {
  phone: string;
}

export interface SendVerificationCodeResponse {
  success: boolean;
  expiresAt: string;
  message: string;
}

export interface VerifyCodeRequest {
  phone: string;
  code: string;
}

export interface VerifyCodeResponse {
  success: boolean;
  verified: boolean;
  message: string;
}

// ==========================================
// Payment Types
// ==========================================

export enum PaymentType {
  SINGLE = 'SINGLE',
  INSTALLMENTS = 'INSTALLMENTS',
}

export interface CreatePaymentPreferenceRequest {
  name: string;
  email: string;
  phone: string;
  type: PaymentType;
  amount: number;
}

export interface CreatePaymentPreferenceResponse {
  success: boolean;
  preferenceId: string;
  initPoint: string;
  message: string;
  paymentType: PaymentType;
  amount: number;
}

// ==========================================
// Custom Payment Link Types
// ==========================================

export enum LinkType {
  SERVICO = 'SERVICO',
  RENOVACAO = 'RENOVACAO',
  CONSULTORIA = 'CONSULTORIA',
  AGENCIA = 'AGENCIA',
}

export interface CreateCustomPaymentLinkRequest {
  amount: number;
  paymentType: PaymentType;
  linkType: LinkType;
  installments?: number;
  description?: string;
  expiresAt?: string;
  maxUses?: number;
  metadata?: Record<string, unknown>;
}

export interface CreateCustomPaymentLinkResponse {
  success: boolean;
  token: string;
  link: string;
  amount: number;
  paymentType: PaymentType;
  linkType: LinkType;
  expiresAt?: string;
  message: string;
}

export interface ValidateCustomPaymentTokenResponse {
  success: boolean;
  valid: boolean;
  data?: {
    token: string;
    amount: number;
    paymentType: PaymentType;
    linkType: LinkType;
    installments: number;
    description?: string;
    expiresAt?: string;
  };
  message: string;
}

export interface CreateCustomPaymentPreferenceRequest {
  token: string;
  name: string;
  email: string;
  phone: string;
}

export interface UpdateCustomPaymentLinkRequest {
  isActive?: boolean;
  expiresAt?: string;
  maxUses?: number;
  description?: string;
}

export interface CustomPaymentLink {
  id: string;
  token: string;
  amount: number;
  paymentType: PaymentType;
  linkType: LinkType;
  installments: number | null;
  description: string | null;
  expiresAt: string | null;
  usedAt: string | null;
  isActive: boolean;
  maxUses: number | null;
  timesUsed: number;
  createdBy: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListCustomPaymentLinksFilters {
  isActive?: boolean;
  createdBy?: string;
  includeExpired?: boolean;
}

// ==========================================
// User Types
// ==========================================

export enum UserRole {
  ADMIN = 'ADMIN',
  GESTOR = 'GESTOR',
  CLIENT = 'CLIENT',
  COLLABORATOR = 'COLLABORATOR',
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  notionPageId: string | null;
  lastAccessAt: string | null;
  createdAt: string;
  updatedAt: string;
  permissions?: UserPermissions;
}

export interface UserAccess {
  id: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
  };
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AccessStats {
  totalAccesses: number;
  uniqueUsers: number;
  accessesByDay: Array<{
    date: string;
    count: number;
  }>;
}

export interface GetAccessesParams {
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

// ==========================================
// Commission Types
// ==========================================

export enum ServiceType {
  FLIGHT = 'FLIGHT',
  FLIGHT_MILES = 'FLIGHT_MILES',
  ACCOMMODATION = 'ACCOMMODATION',
  INSURANCE = 'INSURANCE',
  OTHER_SERVICE = 'OTHER_SERVICE',
}

export enum CommissionStatus {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
}

export interface CommissionRule {
  id: string;
  employeeId: string;
  serviceType: ServiceType;
  isNational: boolean | null; // null para serviços que não diferenciam, true/false para FLIGHT e FLIGHT_MILES

  // Valores fixos por nível (baseado em "Quando foi feito?")
  level1FixedValue: number; // Nível 1: 100% dentro do horário
  level2FixedValue: number; // Nível 2: Cotação dentro e emissão fora
  level3FixedValue: number; // Nível 3: 100% fora do horário

  // Percentuais por nível (aplicados sobre o lucro quando há lucro)
  level1Percentage: number; // Default: 10%
  level2Percentage: number; // Default: 20%
  level3Percentage: number; // Default: 25%

  createdAt: string;
  updatedAt: string;
  employee?: {
    id: string;
    user: {
      name: string;
      email: string;
    };
  };
}

export interface CommissionRecord {
  id: string;
  employeeId: string;
  notionPageId: string; // ID único (original ou reversal)
  originalNotionPageId?: string; // ID da página original no Notion (sempre aponta para a emissão real)
  amount: number; // Valor calculado da comissão
  profitValue: number; // Lucro da emissão
  level: number; // Nível da comissão (1, 2, ou 3)
  percentage: number; // Percentual aplicado
  serviceType: ServiceType;
  status: CommissionStatus; // Status da comissão (sincronizado com Notion)
  isOutHours: boolean; // DEPRECATED: mantido para compatibilidade
  issueDate: string;
  createdAt: string;
  updatedAt: string;
  employee?: {
    id: string;
    user: {
      name: string;
      email: string;
    };
  };
}

export interface CommissionSummary {
  employeeId: string;
  employeeName: string;
  totalCommissions: number;
  totalRecords: number;
  level1Amount: number; // Total Nível 1 (100% Dentro do Horário)
  level2Amount: number; // Total Nível 2 (Cotação dentro e emissão fora)
  level3Amount: number; // Total Nível 3 (100% Fora do Horário)
  byServiceType: Array<{
    serviceType: ServiceType;
    total: number;
    count: number;
  }>;
}

export interface EmployeePaymentSummary {
  employeeId: string;
  employeeName: string;
  pixKey: string | null;
  totalAmount: number;
  isPaid: boolean;
  paymentType: 'SALARY_AND_COMMISSION' | 'PROFIT_ADVANCE';
  user: {
    name: string;
    email: string | null;
    whatsapp: string | null;
  } | null;
  details: {
    // Para colaboradores
    salary?: number;
    commissionsCount?: number;
    commissionsAmount?: number;
    // Para sócios
    profitAdvanceAmount?: number;
    // Descontos
    vtDiscount?: number;
    inssDiscount?: number;
    agreementDiscounts?: number; // Parcelas de acordos de pagamento
    fgts?: number; // FGTS do mês (CLT): custo da empresa, não é descontado
    additionalsAmount?: number; // Adicionais lançados à mão no mês (somam como comissão)
    additionals?: { id: string; description: string; amount: number }[];
    vtPixAmount?: number; // VT pago no Pix junto do pagamento (entra em Adicionais, sem INSS/FGTS)
  };
}

export interface EmployeeBenefitSummary {
  employeeId: string;
  employeeName: string;
  benefitVAVR: number | null;
  benefitHomeOffice: number | null;
  benefitVT: number | null;
  totalBenefits: number;
  user: {
    name: string;
    email: string | null;
    whatsapp: string | null;
  } | null;
}

export interface PixQRCodeResult {
  employeeId: string;
  employeeName: string;
  pixKey: string;
  amount: number;
  qrCode: string; // Base64 image data URL
  payload: string; // String do payload PIX (Pix Copia e Cola)
  beneficiaryName: string;
}

export interface CreateCommissionRuleRequest {
  serviceType: ServiceType;
  isNational?: boolean | null; // null para serviços que não diferenciam, true/false para FLIGHT e FLIGHT_MILES

  // Valores fixos por nível
  level1FixedValue: number;
  level2FixedValue: number;
  level3FixedValue: number;

  // Percentuais por nível (opcionais, com defaults)
  level1Percentage?: number; // Default: 10%
  level2Percentage?: number; // Default: 20%
  level3Percentage?: number; // Default: 25%
}

export interface FinancialMetrics {
  totalGrossRevenue: number;
  totalNetRevenue: number;
  totalFees: number;
  totalSales: number;
  totalCommissions: number;
  netProfit: number;
}

// Labels para tipos de serviço
export const serviceTypeLabels: Record<ServiceType, string> = {
  [ServiceType.FLIGHT]: 'Voo Agência',
  [ServiceType.FLIGHT_MILES]: 'Voo Milhas',
  [ServiceType.ACCOMMODATION]: 'Hospedagem',
  [ServiceType.INSURANCE]: 'Seguro',
  [ServiceType.OTHER_SERVICE]: 'Outros Serviços',
};
